/**
 * MarkdownPreview Component
 * Renders markdown content as formatted HTML with support for:
 * - Headings (h1-h4)
 * - Bold text and links
 * - Lists (ordered and unordered)
 * - Tables
 * - Blockquotes
 * - Horizontal rules
 */

import React from 'react';

interface MarkdownPreviewProps {
  content: string;
  className?: string;
  onCheckboxToggle?: (lineIndex: number, checked: boolean) => void;
}

export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ content, className = '', onCheckboxToggle }) => {
  const renderMarkdown = (markdown: string): React.ReactNode[] => {
    const lines = markdown.split('\n');
    const elements: React.ReactNode[] = [];
    let inTable = false;
    let tableRows: string[][] = [];

    // Generate slug from heading text for anchor links
    const generateSlug = (text: string): string => {
      return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
    };

    const processInlineStyles = (text: string): React.ReactNode => {
      // Combined regex for links, bold text, and inline code
      const inlineRegex = /\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*|`([^`]+)`/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;
      let keyCounter = 0;

      while ((match = inlineRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1] !== undefined && match[2] !== undefined) {
          // Link [text](url)
          const url = match[2];
          const isAnchorLink = url.startsWith('#');
          parts.push(
            <a
              key={`link-${keyCounter++}`}
              href={url}
              className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
              onClick={isAnchorLink ? (e) => {
                e.preventDefault();
                const targetId = url.slice(1);
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                  targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              } : undefined}
              target={!isAnchorLink ? '_blank' : undefined}
              rel={!isAnchorLink ? 'noopener noreferrer' : undefined}
            >
              {match[1]}
            </a>
          );
        } else if (match[3] !== undefined) {
          // Bold text **text**
          parts.push(<strong key={`bold-${keyCounter++}`}>{match[3]}</strong>);
        } else if (match[4] !== undefined) {
          // Inline code `code`
          parts.push(
            <code key={`code-${keyCounter++}`} className="px-1.5 py-0.5 bg-slate-100 text-slate-800 rounded text-sm font-mono">
              {match[4]}
            </code>
          );
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      return parts.length > 0 ? parts : text;
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto my-4">
            <table className="min-w-full border-collapse border border-slate-300">
              <thead>
                <tr className="bg-slate-100">
                  {tableRows[0]?.map((cell, i) => (
                    <th key={i} className="border border-slate-300 px-3 py-2 text-left text-sm font-semibold text-slate-700">
                      {processInlineStyles(cell.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice(2).map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="border border-slate-300 px-3 py-2 text-sm text-slate-600">
                        {processInlineStyles(cell.trim())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
      }
      inTable = false;
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Table detection
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        if (!inTable) {
          inTable = true;
        }
        if (trimmedLine.match(/^\|[\s\-:|]+\|$/)) {
          tableRows.push([]);
          return;
        }
        const PIPE_PLACEHOLDER = '\u0000PIPE\u0000';
        const lineWithPlaceholder = trimmedLine.slice(1, -1).replace(/\\\|/g, PIPE_PLACEHOLDER);
        const cells = lineWithPlaceholder.split('|').map(cell =>
          cell.replace(new RegExp(PIPE_PLACEHOLDER, 'g'), '|')
        );
        tableRows.push(cells);
        return;
      } else if (inTable) {
        flushTable();
      }

      // Headers
      if (trimmedLine.startsWith('# ')) {
        const headingText = trimmedLine.slice(2);
        elements.push(
          <h1 key={index} id={generateSlug(headingText)} className="text-2xl font-bold text-slate-900 mt-6 mb-4 pb-2 border-b border-slate-200">
            {processInlineStyles(headingText)}
          </h1>
        );
      } else if (trimmedLine.startsWith('## ')) {
        const headingText = trimmedLine.slice(3);
        elements.push(
          <h2 key={index} id={generateSlug(headingText)} className="text-xl font-bold text-slate-800 mt-5 mb-3">
            {processInlineStyles(headingText)}
          </h2>
        );
      } else if (trimmedLine.startsWith('### ')) {
        const headingText = trimmedLine.slice(4);
        elements.push(
          <h3 key={index} id={generateSlug(headingText)} className="text-lg font-semibold text-slate-700 mt-4 mb-2">
            {processInlineStyles(headingText)}
          </h3>
        );
      } else if (trimmedLine.startsWith('#### ')) {
        const headingText = trimmedLine.slice(5);
        elements.push(
          <h4 key={index} id={generateSlug(headingText)} className="text-base font-semibold text-slate-600 mt-3 mb-2">
            {processInlineStyles(headingText)}
          </h4>
        );
      }
      // Horizontal rule
      else if (trimmedLine === '---' || trimmedLine === '***') {
        elements.push(<hr key={index} className="my-6 border-slate-200" />);
      }
      // Checkbox list items (must be checked before regular list items)
      else if (trimmedLine.startsWith('- [ ] ') || trimmedLine.startsWith('- [x] ')) {
        const isChecked = trimmedLine.startsWith('- [x] ');
        const itemText = trimmedLine.slice(6);
        const lineIdx = index;
        elements.push(
          <li key={index} className="ml-6 text-slate-600 list-none flex items-start gap-2 my-1">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => onCheckboxToggle?.(lineIdx, !isChecked)}
              readOnly={!onCheckboxToggle}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-600"
            />
            <span className={isChecked ? 'line-through text-slate-400' : ''}>
              {processInlineStyles(itemText)}
            </span>
          </li>
        );
      }
      // List items
      else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        elements.push(
          <li key={index} className="ml-6 text-slate-600 list-disc">
            {processInlineStyles(trimmedLine.slice(2))}
          </li>
        );
      }
      // Numbered list items
      else if (/^\d+\.\s/.test(trimmedLine)) {
        elements.push(
          <li key={index} className="ml-6 text-slate-600 list-decimal">
            {processInlineStyles(trimmedLine.replace(/^\d+\.\s/, ''))}
          </li>
        );
      }
      // Blockquote
      else if (trimmedLine.startsWith('> ')) {
        elements.push(
          <blockquote key={index} className="border-l-4 border-blue-400 pl-4 py-2 my-2 bg-blue-50 text-slate-600 italic">
            {processInlineStyles(trimmedLine.slice(2))}
          </blockquote>
        );
      }
      // Regular paragraph
      else if (trimmedLine.length > 0) {
        elements.push(
          <p key={index} className="text-slate-600 my-2 leading-relaxed">
            {processInlineStyles(trimmedLine)}
          </p>
        );
      }
      // Empty line
      else {
        elements.push(<div key={index} className="h-2" />);
      }
    });

    if (inTable) {
      flushTable();
    }

    return elements;
  };

  return (
    <div className={`prose max-w-none ${className}`}>
      {renderMarkdown(content)}
    </div>
  );
};

export default MarkdownPreview;
